import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { listingFields } from '../../config/configListing';
import { SECTOR_SYNONYMS } from '../../util/sectorSynonyms';

import css from './FieldSectorAutocomplete.module.css';

// SECTOR-AUTOCOMPLETE-01 — combobox autocompletado para el sector
// del listing en EditListingDetailsForm. Sustituye al <FieldSelect>
// nativo. Sólo emite el slug final (madre si no hay sub) en
// publicData.sector — mismo modelo de datos que DATAMODEL-01.
// El campo "subsector" mencionado en el spec original no se almacena
// como publicData separado; el slug seleccionado ya identifica la
// hoja de la taxonomía.

// ─── Helpers ────────────────────────────────────────────────────

const normalize = str =>
  String(str || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const buildIndex = (options, allowParents) => {
  const entries = [];
  for (const parent of options) {
    const subs = parent.suboptions || [];
    if (subs.length === 0 || allowParents) {
      entries.push({
        slug: parent.option,
        label: parent.label,
        parentLabel: null,
        searchTokens: [
          normalize(parent.label),
          ...(SECTOR_SYNONYMS[parent.option] || []).map(normalize),
        ],
      });
    }
    if (subs.length) {
      for (const sub of subs) {
        entries.push({
          slug: sub.option,
          label: sub.label,
          parentLabel: parent.label,
          searchTokens: [
            normalize(sub.label),
            normalize(parent.label),
            ...(SECTOR_SYNONYMS[sub.option] || []).map(normalize),
          ],
        });
      }
    }
  }
  return entries;
};

// Spanish plural variants, ranked by label before synonyms/category matches.
const words = text =>
  normalize(text)
    .trim()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
const variants = word => [
  word,
  ...(word.length > 3 && word.endsWith('s') ? [word.slice(0, -1)] : []),
  ...(word.length > 4 && word.endsWith('es') ? [word.slice(0, -2)] : []),
];
const matchesWords = (text, query) =>
  query.every(q =>
    words(text).some(w => variants(q).some(v => variants(w).some(target => target.startsWith(v))))
  );
const matchEntries = (entries, rawQuery) => {
  const query = words(rawQuery);
  if (!query.length) return entries;
  const score = entry =>
    normalize(entry.label) === normalize(rawQuery).trim()
      ? 0
      : matchesWords(entry.label, query)
      ? 1
      : 2;
  return entries
    .filter(e => e.searchTokens.some(t => matchesWords(t, query)))
    .sort((a, b) => score(a) - score(b));
};

// Collapsed taxonomy browser. Only leaf slugs (or childless parents) are selectable.
const Hierarchy = ({ id, options, onPick, allowParents }) => {
  const [openParent, setOpenParent] = useState(null);
  return (
    <ul className={css.modalList}>
      {options.map(parent => {
        const subs = parent.suboptions || [];
        const isOpen = openParent === parent.option;
        const hasSubs = subs.length > 0;
        return (
          <li key={parent.option} className={css.modalParent}>
            <button
              type="button"
              className={css.modalParentBtn}
              aria-expanded={hasSubs ? isOpen : undefined}
              aria-controls={hasSubs ? `${id}-${parent.option}` : undefined}
              onClick={() =>
                hasSubs ? setOpenParent(isOpen ? null : parent.option) : onPick(parent.option)
              }
            >
              <span>{parent.label}</span>
              {hasSubs ? (
                <span
                  aria-hidden="true"
                  className={classNames(css.caret, {
                    [css.caretOpen]: isOpen,
                  })}
                >
                  ▾
                </span>
              ) : null}
            </button>
            {hasSubs && isOpen ? (
              <ul id={`${id}-${parent.option}`} className={css.modalSubList}>
                {allowParents && (
                  <li>
                    <button
                      type="button"
                      className={css.modalSubBtn}
                      onClick={() => onPick(parent.option)}
                    >
                      <FormattedMessage
                        id="FieldSectorAutocomplete.wholeCategory"
                        values={{ sector: parent.label }}
                      />
                    </button>
                  </li>
                )}
                {subs.map(sub => (
                  <li key={sub.option}>
                    <button
                      type="button"
                      className={css.modalSubBtn}
                      onClick={() => onPick(sub.option)}
                    >
                      {sub.label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

// ─── Combobox UI ────────────────────────────────────────────────

const ComboboxUI = ({
  id,
  value,
  onChange,
  onBlur,
  onFocus,
  label,
  helpText,
  errorId,
  allowParents,
}) => {
  const intl = useIntl();
  const sectorField = useMemo(() => listingFields.find(f => f.key === 'sector'), []);
  const options = sectorField?.enumOptions || [];
  const entries = useMemo(() => buildIndex(options, allowParents), [options, allowParents]);
  const slugToEntry = useMemo(() => {
    const m = {};
    for (const e of entries) m[e.slug] = e;
    return m;
  }, [entries]);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  const wrapperRef = useRef(null);
  // Safari may blur an input without focusing the clicked button. Keep the
  // popup mounted through the pointer's click (including delayed touch clicks).
  const pointerInside = useRef(false);

  useEffect(() => {
    const onDocClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        pointerInside.current = false;
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDocClick);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('pointerdown', onDocClick);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, []);

  const matches = useMemo(() => matchEntries(entries, query), [entries, query]);
  const noMatches = query.trim().length > 0 && matches.length === 0;

  const selectedEntry = value
    ? slugToEntry[value] || options.find(option => option.option === value)
    : null;

  const pick = useCallback(
    slug => {
      onChange(slug);
      setQuery('');
      inputRef.current?.focus();
      setOpen(false);
    },
    [onChange]
  );

  const onKeyDown = e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      inputRef.current?.focus();
      setOpen(false);
      return;
    }
    if (!open) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    const buttons = Array.from(panelRef.current?.querySelectorAll('button') || []);
    const current = buttons.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next =
        e.key === 'ArrowDown'
          ? Math.min(current + 1, buttons.length - 1)
          : current <= 0
          ? -1
          : current - 1;
      if (next < 0) inputRef.current?.focus();
      else buttons[next]?.focus();
    } else if (e.key === 'Enter' && e.target === inputRef.current) {
      e.preventDefault();
      if (query.trim() && matches[0]) pick(matches[0].slug);
    }
  };

  const triggerLabel = selectedEntry
    ? selectedEntry.parentLabel
      ? `${selectedEntry.parentLabel} → ${selectedEntry.label}`
      : selectedEntry.label
    : null;

  return (
    <div
      className={css.root}
      ref={wrapperRef}
      onKeyDown={e => {
        pointerInside.current = false;
        onKeyDown(e);
      }}
      onBlur={e => {
        if (!pointerInside.current && !e.currentTarget.contains(e.relatedTarget)) {
          setOpen(false);
          onBlur();
        }
      }}
    >
      {label ? (
        <label htmlFor={id} className={css.fieldLabel}>
          {label}
        </label>
      ) : null}

      <div className={classNames(css.combobox, { [css.comboboxOpen]: open })}>
        <input
          id={id}
          ref={inputRef}
          role="combobox"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? `${id}-categories` : undefined}
          type="text"
          aria-describedby={
            [helpText ? `${id}-help` : null, errorId].filter(Boolean).join(' ') || undefined
          }
          aria-invalid={errorId ? true : undefined}
          className={css.input}
          placeholder={
            triggerLabel && !open
              ? ''
              : intl.formatMessage({
                  id: 'FieldSectorAutocomplete.placeholder',
                })
          }
          value={!open && triggerLabel ? triggerLabel : query}
          onChange={e => {
            setQuery(e.target.value);
            if (value) onChange('');
            setOpen(true);
          }}
          onClick={() => setOpen(true)}
          onFocus={() => {
            onFocus();
            setOpen(true);
          }}
          autoComplete="off"
        />
      </div>

      {open ? (
        <div
          className={css.panel}
          id={`${id}-categories`}
          ref={panelRef}
          role="dialog"
          aria-label={label}
          onPointerDownCapture={() => {
            pointerInside.current = true;
          }}
          onMouseDownCapture={() => {
            pointerInside.current = true;
          }}
          onClickCapture={() => {
            pointerInside.current = false;
          }}
        >
          {noMatches ? (
            <div className={css.empty}>
              <p className={css.emptyText}>
                <FormattedMessage id="FieldSectorAutocomplete.noMatches" values={{ query }} />
              </p>
              <button
                type="button"
                className={css.emptyAction}
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
              >
                <FormattedMessage id="FieldSectorAutocomplete.viewAll" />
              </button>
            </div>
          ) : !query.trim() ? (
            <Hierarchy id={id} options={options} onPick={pick} allowParents={allowParents} />
          ) : (
            <ul className={css.list}>
              {matches.map(m => (
                <li key={m.slug}>
                  <button type="button" className={css.option} onClick={() => pick(m.slug)}>
                    {m.parentLabel ? (
                      <span className={css.optionParent}>{m.parentLabel}</span>
                    ) : null}
                    <span className={css.optionLabel}>{m.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {helpText ? (
        <p id={`${id}-help`} className={css.helpText}>
          {helpText}
        </p>
      ) : null}
    </div>
  );
};

// ─── react-final-form adapter ───────────────────────────────────

const FieldSectorAutocomplete = props => {
  const { name, label, helpText, validate, id, allowParents = false } = props;
  const generatedId = React.useId();
  const inputId = id || generatedId;
  return (
    <Field name={name} validate={validate}>
      {({ input, meta }) => (
        <>
          <ComboboxUI
            id={inputId}
            allowParents={allowParents}
            value={input.value || ''}
            onChange={v => input.onChange(v)}
            onBlur={input.onBlur}
            onFocus={input.onFocus}
            label={label}
            helpText={helpText}
            errorId={meta.touched && meta.error ? `${inputId}-error` : undefined}
          />
          {meta.touched && meta.error ? (
            <p id={`${inputId}-error`} className={css.error}>
              {meta.error}
            </p>
          ) : null}
        </>
      )}
    </Field>
  );
};

export default FieldSectorAutocomplete;
